import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Assignment as TemplateIcon,
  AddCircleOutline as BlankProjectIcon,
  ArrowForwardRounded as ArrowForwardIcon,
  AutoAwesome as StartProjectIcon,
  Bolt as ActionIcon,
  CheckCircleOutline as SelectedIcon,
  Dashboard as WidgetIcon,
  Hub as ConnectorIcon,
  Search as SearchIcon,
  Storage as LocalTemplateIcon,
  Upload as UploadIcon,
  MoreVert as MoreVertIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteIcon,
  Star as FavoriteIcon,
  StarBorder as FavoriteBorderIcon
} from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useUser, useWebSocket } from './WebSocketContext';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';
import {
  clearCaseCreation,
  startCaseCreation,
} from '../Utils/caseCreationState';
import { buildTemplateDownloadFilename, buildTemplateExportData } from '../Utils/templateExport';
import { applySharedTemplateStatuses } from '../Utils/templateSharedStatus';
import { FLOWS_PROJECT_VIEW, saveProjectViewForSession } from '../Utils/projectViewSession';

const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const lightPurple = '#f0f0ff';
let nextTemplateLoadRequestId = 0;
export const isSafeTemplateImage = (value) => (
  typeof value === 'string'
  && /^data:image\/(?:png|jpeg|jpg|webp);base64,[A-Za-z0-9+/]+={0,2}$/i.test(value)
);

const parseTemplateList = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [value];
  }
};

const getTemplateTitle = (template) => (
  String(template?.template_title || template?.name || '').trim()
);

const getTemplateFavoriteIdentity = (template) => {
  const isSharedTemplate = template?.source_type === 'shared' || Boolean(template?.shared_template_id);
  const templateType = isSharedTemplate ? 'shared' : 'local';
  const templateId = String(
    isSharedTemplate
      ? (template?.shared_template_id || template?.id || '')
      : (template?.id ?? '')
  ).trim();
  return {
    templateType,
    templateId,
    key: `${templateType}:${templateId}`,
  };
};

const getTemplateDescription = (template) => (
  String(
    template?.template_description
      || template?.description
      || template?.project_description
      || template?.project_goal
      || ''
  ).trim()
);

const normalizeConnectorKey = (value) => (
  String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '')
);

const parseTemplateObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return {};
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const connectorLabels = {
  airtable: 'Airtable',
  api: 'Generic API',
  csv: 'CSV',
  dropbox: 'Dropbox',
  excel: 'Excel',
  folders: 'Folders',
  folder: 'Folders',
  gmail: 'Google Gmail',
  googledrive: 'Google Drive',
  googlenews: 'Google News',
  gnews: 'GNews.io',
  mcp: 'MCP Connector',
  mediastack: 'Mediastack',
  newsapi: 'NewsAPI',
  newsapiai: 'NewsAPI.ai',
  notion: 'Notion',
  onedrive: 'OneDrive',
  outlookapi: 'Outlook API',
  outlooklocal: 'Outlook',
  pluginexamplepolling: 'Plugin Example Polling',
  pythonscript: 'Python Script',
  reddit: 'Reddit',
  salesforce: 'Salesforce',
  slack: 'Slack',
  test: 'Test Connector',
  whatsapp: 'WhatsApp',
  webscraper: 'Web Scraper',
};

const getTemplateConnectorNames = (template, connectorPlugins = []) => {
  const pluginNamesByType = new Map();
  const registerPluginName = (plugin) => {
    if (!plugin || typeof plugin !== 'object') return;
    const manifest = parseTemplateObject(plugin.plugin_json ?? plugin.pluginJson);
    const type = String(plugin.type || manifest.type || '').trim();
    const name = String(plugin.name || manifest.name || '').trim();
    const normalizedType = normalizeConnectorKey(type);
    if (normalizedType && name) pluginNamesByType.set(normalizedType, name);
  };

  (Array.isArray(connectorPlugins) ? connectorPlugins : []).forEach(registerPluginName);
  parseTemplateList(
    template?.embedded_plugins
      ?? template?.embeddedPlugins
      ?? template?.plugins
  ).forEach(registerPluginName);

  return parseTemplateList(
    template?.datasource_suggestions
      ?? template?.datasourceSuggestions
      ?? template?.suggested_datasources
  )
    .map((entry) => {
      const connectorType = entry && typeof entry === 'object'
        ? entry.connector_type || entry.connectorType || entry.type || ''
        : entry;
      const normalizedType = normalizeConnectorKey(connectorType);
      if (normalizedType && pluginNamesByType.has(normalizedType)) {
        return pluginNamesByType.get(normalizedType);
      }
      if (entry && typeof entry === 'object') {
        return entry.connector_name
          || entry.connectorName
          || entry.name
          || entry.title
          || connectorType;
      }
      return connectorType;
    })
    .map((entry) => String(entry || '').trim())
    .filter(Boolean)
    .map((entry) => {
      const normalizedKey = normalizeConnectorKey(entry);
      if (connectorLabels[normalizedKey]) return connectorLabels[normalizedKey];

      // Keep a readable fallback for templates created before plugin names were stored.
      const readableName = entry
        .replace(/^plugin[\s_-]*auto[\s_-]*/i, '')
        .replace(/[\s_-]*connector(?:[\s_-]+[a-f0-9]{6,})?$/i, '');
      return readableName
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());
    })
    .filter((entry, index, entries) => (
      entries.findIndex((candidate) => normalizeConnectorKey(candidate) === normalizeConnectorKey(entry)) === index
    ));
};

const getTemplateItemCount = (value) => parseTemplateList(value).length;

const getTemplateSetupRequirements = (template) => {
  const modelPreferences = parseTemplateObject(
    template?.model_preferences ?? template?.modelPreferences
  );
  return parseTemplateList(
    modelPreferences.template_setup_requirements
      ?? modelPreferences.templateSetupRequirements
  ).filter((requirement) => requirement && typeof requirement === 'object');
};

const getTemplateSetupCounts = (template) => {
  const modelPreferences = parseTemplateObject(
    template?.model_preferences ?? template?.modelPreferences
  );
  const hasRequirements = Object.prototype.hasOwnProperty.call(modelPreferences, 'template_setup_requirements')
    || Object.prototype.hasOwnProperty.call(modelPreferences, 'templateSetupRequirements');
  const requirements = getTemplateSetupRequirements(template);
  const inputNodeKeys = new Set(requirements
    .filter((requirement) => requirement.node_kind === 'input')
    .map((requirement) => requirement.node_key || requirement.id || `${requirement.input_type || ''}:${requirement.input_ordinal ?? ''}`)
    .filter(Boolean));
  return {
    known: template?.source_type !== 'shared' || hasRequirements,
    steps: requirements.length,
    sources: inputNodeKeys.size,
    fields: requirements.filter((requirement) => Boolean(requirement.field_path)).length,
    optional: requirements.filter((requirement) => requirement.required === false).length,
  };
};

const getTemplateDashboardSettings = (template) => {
  const modelPreferences = parseTemplateObject(
    template?.model_preferences ?? template?.modelPreferences
  );
  const layoutPreferences = parseTemplateObject(
    modelPreferences.widget_layout_preferences
      ?? modelPreferences.widgetLayoutPreferences
      ?? template?.widget_layout_preferences
      ?? template?.widgetLayoutPreferences
  );

  return parseTemplateObject(
    layoutPreferences.layout_settings
      ?? layoutPreferences.layoutSettings
      ?? modelPreferences.dashboard_layout_settings
      ?? modelPreferences.dashboardLayoutSettings
      ?? template?.layout_settings
      ?? template?.layoutSettings
  );
};

const getSafeTabColor = (value) => {
  const color = String(value || '').trim();
  return /^#[0-9a-f]{6}$/i.test(color) ? color : '';
};

const getTemplateDashboardTabs = (template) => {
  const layoutSettings = getTemplateDashboardSettings(template);
  const rawTabs = layoutSettings.dashboardTabs ?? layoutSettings.dashboard_tabs;
  const tabs = parseTemplateList(rawTabs)
    .filter((tab) => tab && typeof tab === 'object')
    .map((tab) => ({
      id: String(tab.id || '').trim(),
      name: String(tab.name || '').trim(),
      color: getSafeTabColor(tab.color),
    }))
    .filter((tab) => tab.id && tab.name);

  return tabs.length > 0 ? tabs : [{ id: 'default', name: 'Dashboard', color: '' }];
};

const TemplateSelectionDialog = ({
  open,
  onClose,
  onSelectTemplate,
  onStartBlankProject,
  initialTemplateId = null,
  renderBehindProjectSidebar = false,
}) => {
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages, serverInfo } = useWebSocket();
  const { isAdmin, isEdit, isPublicUser } = useUser();
  const [templates, setTemplates] = useState([]);
  const [favoriteTemplateKeys, setFavoriteTemplateKeys] = useState([]);
  const [sharedTemplates, setSharedTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [projectTitle, setProjectTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [importFeedback, setImportFeedback] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [pendingImportData, setPendingImportData] = useState(null);
  const [showUnverified, setShowUnverified] = useState(false);
  const [sharedCatalogWarning, setSharedCatalogWarning] = useState(null);
  const [templateMenuAnchorEl, setTemplateMenuAnchorEl] = useState(null);
  const [templateMenuTarget, setTemplateMenuTarget] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);
  const [sharingTemplateId, setSharingTemplateId] = useState(null);
  const selectedTemplateSetupCounts = getTemplateSetupCounts(selectedTemplate);
  const selectedTemplateHasSetup = selectedTemplateSetupCounts.steps > 0 || !selectedTemplateSetupCounts.known;
  const [downloadingTemplateId, setDownloadingTemplateId] = useState(null);
  const pendingTemplateLoad = useRef({ id: 0, local: false, shared: false, complete: false });

  const canManageTemplateActions = !isPublicUser && (isAdmin || isEdit);

  const handleClose = useCallback(() => {
    pendingTemplateLoad.current = {
      id: ++nextTemplateLoadRequestId, local: false, shared: false, complete: false,
    };
    setLoading(false);
    setSelectedTemplate(null);
    setError(null);
    setProjectTitle('');
    setCreating(false);
    setSearchTerm('');
    setImporting(false);
    setImportFeedback(null);
    setDuplicateDialogOpen(false);
    setPendingImportData(null);
    setShowUnverified(false);
    setSharedCatalogWarning(null);
    setTemplateMenuAnchorEl(null);
    setTemplateMenuTarget(null);
    setTemplateToDelete(null);
    setDeleteDialogOpen(false);
    clearCaseCreation();
    onClose();
  }, [onClose]);

  // Load templates when dialog opens
  const loadTemplates = useCallback(() => {
    const requestId = ++nextTemplateLoadRequestId;
    pendingTemplateLoad.current = { id: requestId, local: false, shared: false, complete: false };
    setLoading(true);
    setError(null);
    setSharedCatalogWarning(null);
    setSharedTemplates([]);
    sendMessage({
      command: 'getAllTemplates',
      data: { request_id: requestId },
    });
    sendMessage({
      command: 'getSharedTemplates',
      data: { include_unverified: showUnverified, request_id: requestId },
    });
  }, [sendMessage, showUnverified]);

  const handleImportTemplate = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const fileData = JSON.parse(e.target.result);
          if (fileData.templates && Array.isArray(fileData.templates) && fileData.templates.length > 1) {
            setPendingImportData({ type: 'multiple', data: fileData.templates });
          } else {
            const templateData = fileData.templates ? fileData.templates[0] : fileData;
            setPendingImportData({ type: 'single', data: templateData });
          }
          setDuplicateDialogOpen(true);
          setImportFeedback(null);
          setError(null);
        } catch {
          setImportFeedback({
            severity: 'error',
            message: t('template.invalid_json_file', 'Invalid JSON file format')
          });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleConfirmedImport = (skipDuplicates) => {
    if (!pendingImportData) return;

    setDuplicateDialogOpen(false);
    setImporting(true);
    setImportFeedback(null);
    setError(null);

    if (pendingImportData.type === 'multiple') {
      sendMessage({
        command: 'importTemplates',
        data: {
          templates_data: pendingImportData.data,
          skip_duplicates: skipDuplicates
        }
      });
    } else {
      sendMessage({
        command: 'importTemplate',
        data: {
          template_data: pendingImportData.data,
          skip_duplicates: skipDuplicates
        }
      });
    }
  };

  const handleCancelledImport = () => {
    setDuplicateDialogOpen(false);
    setPendingImportData(null);
    setImporting(false);
  };

  const availableTemplates = useMemo(() => {
    const localSharedIds = new Set(
      templates
        .map((template) => template?.shared_template_id)
        .filter(Boolean)
        .map((templateId) => String(templateId)),
    );
    const remoteTemplates = sharedTemplates
      .filter((template) => !localSharedIds.has(String(template?.id)))
      .map((template) => ({
        ...template,
        source_type: 'shared',
        shared_template_id: template.id,
        template_title: template.name,
        template_description: template.description,
        shared_verification_status: template.verification_status,
      }));

    return [...templates, ...remoteTemplates];
  }, [templates, sharedTemplates]);

  // Filter templates based on search term
  const filteredTemplates = useMemo(() => {
    if (!searchTerm.trim()) return availableTemplates;
    
    const searchLower = searchTerm.toLowerCase();
    return availableTemplates.filter(template => {
      const searchableText = [
        getTemplateTitle(template),
        getTemplateDescription(template),
        ...getTemplateConnectorNames(template, serverInfo?.connectorPlugins),
        ...getTemplateDashboardTabs(template).map((tab) => tab.name),
      ].join(' ').toLowerCase();

      return searchableText.includes(searchLower);
    });
  }, [availableTemplates, searchTerm, serverInfo?.connectorPlugins]);

  const favoriteTemplateKeySet = useMemo(
    () => new Set(favoriteTemplateKeys),
    [favoriteTemplateKeys]
  );

  const orderedFilteredTemplates = useMemo(() => {
    const favorites = [];
    const otherTemplates = [];
    filteredTemplates.forEach((template) => {
      const { key } = getTemplateFavoriteIdentity(template);
      (favoriteTemplateKeySet.has(key) ? favorites : otherTemplates).push(template);
    });
    return [...favorites, ...otherTemplates];
  }, [favoriteTemplateKeySet, filteredTemplates]);

  useEffect(() => {
    if (open) {
      loadTemplates();
      // Reset search when dialog opens
      setSearchTerm('');
    }
  }, [open, loadTemplates]);

  // Handle template command responses from the HTTP bridge.
  useEffect(() => {
    const finishTemplateLoad = () => {
      const pending = pendingTemplateLoad.current;
      if (pending.complete || !pending.local || !pending.shared) return;
      pending.complete = true;
      setLoading(false);
      if (pending.hasLinkedLocal) {
        sendMessage({ command: 'refreshSharedTemplateStatuses' });
      }
    };

    const messageHandler = (message) => {
      const data = message.data || {};
      
      switch (message.command) {
        case 'getAllTemplates':
          if ((message.request_id ?? data.request_id) !== pendingTemplateLoad.current.id) break;
          if (data.status === 'success') {
            const templates = data.templates || [];
            setTemplates(templates);
            setFavoriteTemplateKeys(
              Array.isArray(data.favorite_template_keys) ? data.favorite_template_keys : []
            );
            if (initialTemplateId !== null && initialTemplateId !== undefined) {
              const initialTemplate = templates.find(
                (template) => String(template?.id) === String(initialTemplateId)
              );
              if (initialTemplate) {
                setSelectedTemplate(initialTemplate);
                setProjectTitle(getTemplateTitle(initialTemplate));
              }
            }
            pendingTemplateLoad.current.hasLinkedLocal = templates.some(
              (template) => template.shared_template_id
            );
          } else {
            const errorMessage = data.message || 'Failed to load templates';
            setError(errorMessage);
          }
          pendingTemplateLoad.current.local = true;
          finishTemplateLoad();
          break;
        case 'refreshSharedTemplateStatuses':
          if (data.status === 'success') {
            setTemplates((current) => applySharedTemplateStatuses(current, data.templates));
            setSelectedTemplate((current) => (
              current ? applySharedTemplateStatuses([current], data.templates)[0] : current
            ));
          }
          break;
        case 'setTemplateFavorite':
          if (data.status !== 'success') {
            setImportFeedback({
              severity: 'error',
              message: data.message || t('template.favorite_save_error', 'Could not save this favorite.'),
            });
            loadTemplates();
          }
          break;
        case 'getSharedTemplates':
          if ((message.request_id ?? data.request_id) !== pendingTemplateLoad.current.id) break;
          if (data.status === 'success') {
            setSharedTemplates(data.templates || []);
            setSharedCatalogWarning(null);
          } else {
            setSharedCatalogWarning(
              t(
                'template.shared_catalog_unavailable',
                'Shared template catalog is disconnected. Local templates remain available.'
              )
            );
          }
          pendingTemplateLoad.current.shared = true;
          finishTemplateLoad();
          break;
        case 'shareTemplate': {
          setSharingTemplateId(null);
          if (data.status === 'success') {
            const verificationStatus = String(
              data.template?.verification_status || data.verification_status || '',
            ).toLowerCase();
            setImportFeedback({
              severity: 'success',
              message: verificationStatus === 'verified'
                ? t('global_setting.template_shared_verified', 'Template shared and verified')
                : t('global_setting.template_shared', 'Template shared and submitted for verification'),
            });
            loadTemplates();
          } else {
            setImportFeedback({
              severity: 'error',
              message: data.message || t('global_setting.template_share_error', 'Error sharing template'),
            });
          }
          break;
        }
        case 'deleteTemplate':
          setDeletingTemplateId(null);
          if (data.status === 'success') {
            const deletedTemplateId = data.template_id;
            setTemplates((currentTemplates) => currentTemplates.filter(
              (template) => template.id !== deletedTemplateId,
            ));
            setDeleteDialogOpen(false);
            setTemplateToDelete(null);
            setSelectedTemplate((currentTemplate) => (
              currentTemplate?.id === deletedTemplateId ? null : currentTemplate
            ));
            setImportFeedback({
              severity: 'success',
              message: t('global_setting.template_deleted', 'Template deleted successfully'),
            });
            loadTemplates();
          } else {
            setImportFeedback({
              severity: 'error',
              message: data.message || t('global_setting.template_delete_error', 'Error deleting template'),
            });
          }
          break;
        case 'loadTemplate':
          if (data.status === 'success') {
            const template = data.template;
            onSelectTemplate(template);
            handleClose();
          } else {
            const errorMessage = data.message || 'Failed to load template';
            setError(errorMessage);
          }
          break;
        case 'importSharedTemplate':
          if (data.status === 'success' && data.template_id) {
            sendMessage({
              command: 'createProjectFromTemplate',
              data: {
                template_id: data.template_id,
                title: projectTitle.trim(),
              },
            });
          } else {
            setCreating(false);
            setError(data.message || 'Failed to import shared template');
          }
          break;
        case 'createProjectFromTemplate':
          setCreating(false);
          if (data.status === 'success') {
            // Project created successfully, navigate to it
            const projectId = data.project_id;
            clearCaseCreation();
            if (projectId && window.location) {
              if (selectedTemplateHasSetup) {
                saveProjectViewForSession(projectId, FLOWS_PROJECT_VIEW);
              }
              const setupQuery = selectedTemplateHasSetup ? '?openTemplateSetup=true' : '';
              window.location.href = `/case/${projectId}${setupQuery}`;
            }
            handleClose();
          } else {
            clearCaseCreation();
            const errorMessage = data.message || 'Failed to create project from template';
            setError(errorMessage);
          }
          break;
        case 'importTemplate':
          setImporting(false);
          setPendingImportData(null);
          if (data.status === 'success') {
            setImportFeedback({
              severity: 'success',
              message: data.message || t('template.template_imported', 'Template imported successfully')
            });
            loadTemplates();
          } else if (data.status === 'skipped') {
            setImportFeedback({
              severity: 'info',
              message: data.message || t('template.template_skipped', 'Template already exists - import skipped')
            });
          } else {
            setImportFeedback({
              severity: 'error',
              message: data.message || t('template.template_import_error', 'Failed to import template')
            });
          }
          break;
        case 'importTemplates': {
          setImporting(false);
          setPendingImportData(null);

          if (data.status === 'success' || data.status === 'partial_success') {
            const importedCount = Number(data.imported_count || 0);
            const skippedCount = Number(data.skipped_count || 0);
            const failedCount = Number(data.failed_count || 0);
            const feedbackMessage =
              data.message ||
              t('template.bulk_import_result', {
                defaultValue: 'Imported {{imported}} templates. Skipped: {{skipped}}. Failed: {{failed}}.',
                imported: importedCount,
                skipped: skippedCount,
                failed: failedCount
              });
            setImportFeedback({
              severity: data.status === 'partial_success' ? 'warning' : 'success',
              message: feedbackMessage
            });

            if (importedCount > 0) {
              loadTemplates();
            }
          } else if (data.status === 'skipped') {
            setImportFeedback({
              severity: 'info',
              message: data.message || t('template.bulk_import_skipped', 'All templates already exist - nothing imported')
            });
          } else {
            setImportFeedback({
              severity: 'error',
              message: data.message || t('template.bulk_import_error', 'Failed to import templates')
            });
          }
          break;
        }
        default:
          // Ignore other commands
          break;
      }
    };

    const unsubscribe = subscribeToMessages(messageHandler);
    return unsubscribe;
  }, [subscribeToMessages, onSelectTemplate, handleClose, loadTemplates, projectTitle, selectedTemplateHasSetup, sendMessage, t, initialTemplateId]);

  const handleStartBlankProject = () => {
    handleClose();
    onStartBlankProject?.();
  };

  const handleCreateProject = () => {
    if (selectedTemplate && projectTitle.trim()) {
      if (!sendMessage) {
        clearCaseCreation();
        setError(t('addcase.datasource_upload_error', 'Unable to create the project right now. Please try again.'));
        return;
      }
      setCreating(true);
      setError(null);
      startCaseCreation();
      if (selectedTemplate.source_type === 'shared') {
        sendMessage({
          command: 'importSharedTemplate',
          data: {
            shared_template_id: selectedTemplate.shared_template_id || selectedTemplate.id,
            skip_duplicates: false,
          },
        });
        return;
      }
      sendMessage({
        command: 'createProjectFromTemplate',
        data: {
          template_id: selectedTemplate.id,
          title: projectTitle.trim()
        }
      });
    }
  };

  const handleTemplateClick = (template) => {
    const currentTitle = projectTitle.trim();
    const previousTemplateTitle = getTemplateTitle(selectedTemplate);
    setSelectedTemplate(template);
    if (!currentTitle || currentTitle === previousTemplateTitle) {
      setProjectTitle(getTemplateTitle(template));
    }
  };

  const handleToggleTemplateFavorite = (event, template) => {
    event.stopPropagation();
    const { templateType, templateId, key } = getTemplateFavoriteIdentity(template);
    if (!templateId) return;

    const isFavorite = favoriteTemplateKeys.includes(key);
    setFavoriteTemplateKeys((currentKeys) => (
      isFavorite
        ? currentKeys.filter((favoriteKey) => favoriteKey !== key)
        : [...currentKeys, key]
    ));
    sendMessage({
      command: 'setTemplateFavorite',
      data: {
        template_type: templateType,
        template_id: templateId,
        is_favorite: !isFavorite,
      },
    });
  };

  const handleTemplateMenuOpen = (event, template) => {
    event.stopPropagation();
    setTemplateMenuAnchorEl(event.currentTarget);
    setTemplateMenuTarget(template);
  };

  const handleTemplateMenuClose = () => {
    setTemplateMenuAnchorEl(null);
    setTemplateMenuTarget(null);
  };

  const handleShareTemplate = (template) => {
    if (
      !canManageTemplateActions
      || !template
      || template.source_type === 'shared'
      || template.id === undefined
      || sharingTemplateId !== null
      || (
        template.share_status === 'published'
        && Number(template.version || 1) <= Number(template.shared_version || 0)
      )
    ) {
      return;
    }
    handleTemplateMenuClose();
    setSharingTemplateId(template.id);
    sendMessage({
      command: 'shareTemplate',
      data: { template_id: template.id },
    });
  };

  const handleDownloadTemplate = (template) => {
    if (!template || template.source_type === 'shared' || downloadingTemplateId !== null) {
      return;
    }

    handleTemplateMenuClose();
    setDownloadingTemplateId(template.id);
    try {
      const exportData = {
        ...buildTemplateExportData(template),
        exported_at: new Date().toISOString(),
      };
      const dataBlob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildTemplateDownloadFilename(template);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setImportFeedback({
        severity: 'success',
        message: t('global_setting.template_exported', 'Template exported successfully'),
      });
    } catch (downloadError) {
      console.error('Unable to export template:', downloadError);
      setImportFeedback({
        severity: 'error',
        message: t('global_setting.export_error', 'Error exporting template'),
      });
    } finally {
      setDownloadingTemplateId(null);
    }
  };

  const handleDeleteTemplate = (template) => {
    if (!canManageTemplateActions || !template || template.source_type === 'shared') {
      return;
    }
    handleTemplateMenuClose();
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (
      !canManageTemplateActions
      || !templateToDelete
      || deletingTemplateId !== null
    ) {
      return;
    }
    setDeletingTemplateId(templateToDelete.id);
    sendMessage({
      command: 'deleteTemplate',
      data: { template_id: templateToDelete.id },
    });
  };

  const handleDeleteCancel = () => {
    if (deletingTemplateId !== null) return;
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const renderBlankProjectCard = () => (
    <Grid item xs={12} sm={6} md={4} key="blank-project">
      <Card
        role="button"
        tabIndex={0}
        onClick={handleStartBlankProject}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleStartBlankProject();
          }
        }}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 248,
          textAlign: 'left',
          cursor: 'pointer',
          border: '2px dashed rgba(87, 87, 176, 0.38)',
          borderRadius: '12px',
          background: 'linear-gradient(145deg, #f7f6ff 0%, #ffffff 72%)',
          transition: 'all 0.25s ease',
          '&:hover, &:focus-visible': {
            borderColor: primaryColor,
            boxShadow: '0 6px 20px rgba(87, 87, 176, 0.14)',
            transform: 'translateY(-2px)',
            outline: 'none',
          },
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 2.5,
            '&:last-child': { pb: 2.5 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: primaryColor,
                backgroundColor: 'rgba(87, 87, 176, 0.12)',
              }}
            >
              <BlankProjectIcon sx={{ fontSize: 26 }} />
            </Box>
            <Chip
              size="small"
              label={t('template.always_available', 'Always available')}
              sx={{
                color: primaryColor,
                backgroundColor: 'rgba(87, 87, 176, 0.1)',
                fontWeight: 700,
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700, lineHeight: 1.25 }}>
            {t('template.blank_project_title', 'Start with a blank project')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, lineHeight: 1.5 }}>
            {t('template.blank_project_description', 'Build from scratch and choose your data sources yourself.')}
          </Typography>
          <Button
            tabIndex={-1}
            endIcon={<ArrowForwardIcon />}
            sx={{
              mt: 'auto',
              alignSelf: 'flex-start',
              px: 0,
              color: primaryColor,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            {t('template.start_blank_project', 'Start blank')}
          </Button>
        </CardContent>
      </Card>
    </Grid>
  );

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };
  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      sx={{
        zIndex: (theme) => renderBehindProjectSidebar
          ? Math.max(0, (theme.zIndex?.mobileStepper || 1000) - 1)
          : theme.zIndex.modal,
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 28px rgba(87, 87, 176, 0.15)',
          border: `1px solid ${lightPurple}`,
          width: '100%',
          maxHeight: 'calc(100vh - 32px)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: lightPurple,
          color: primaryColor,
          fontWeight: 'bold',
          borderBottom: `1px solid ${lightPurple}`,
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StartProjectIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" component="div">
            {t('template.start_project_title', 'Start your project')}
          </Typography>
        </Box>
        <CloseButton
          onClick={handleClose}
          sx={{
            color: primaryColor,
            '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.1)' }
          }}
        />
      </DialogTitle>

      <DialogContent sx={{
        pt: { xs: 2.5, sm: 3 },
        px: { xs: 2, sm: 3 },
        pb: 2.5,
        maxHeight: 'calc(100vh - 170px)',
        overflowY: 'auto',
        '&.MuiDialogContent-root': {
          paddingTop: { xs: '20px !important', sm: '24px !important' }
        }
      }}>
        {importFeedback && (
          <Alert
            severity={importFeedback.severity || 'info'}
            sx={{ mb: 2 }}
            onClose={() => setImportFeedback(null)}
          >
            {importFeedback.message}
          </Alert>
        )}

        <>
            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button
                    size="small"
                    onClick={loadTemplates}
                    sx={{ color: 'error.main' }}
                  >
                    {t('template.retry')}
                  </Button>
                }
              >
                {error}
              </Alert>
            )}

            {sharedCatalogWarning && (
              <Box
                role="status"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  maxWidth: '100%',
                  mb: 1.25,
                  px: 0.9,
                  py: 0.35,
                  color: 'rgba(143, 89, 0, 0.9)',
                  backgroundColor: 'rgba(255, 183, 77, 0.08)',
                  borderRadius: 1,
                  fontSize: '0.72rem',
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: 'inherit', fontSize: 'inherit', lineHeight: 1.35 }}
                >
                  {sharedCatalogWarning}
                </Typography>
              </Box>
            )}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ color: primaryColor }} />
                <Typography sx={{ ml: 2, color: 'text.secondary' }}>
                  {t('template.loading_templates')}
                </Typography>
              </Box>
            )}

            {!loading && availableTemplates.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                      {t('template.available_templates', 'Available templates')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.35 }}>
                      {t('template.select_template_description')}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={t('template.template_count', '{{count}} available', { count: availableTemplates.length + 1 })}
                    sx={{
                      color: primaryColor,
                      backgroundColor: 'rgba(87, 87, 176, 0.1)',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 2 },
                    mb: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t('template.search_templates')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(250, 250, 255, 0.5)',
                        '&:hover fieldset': {
                          borderColor: primaryColor
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: primaryColor
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, color: 'text.secondary' }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                      endAdornment: searchTerm.trim() && (
                        <CloseButton
                          size="small"
                          onClick={() => setSearchTerm('')}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: primaryColor }
                          }}
                        />
                      )
                    }}
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        size="small"
                        checked={showUnverified}
                        onChange={(event) => setShowUnverified(event.target.checked)}
                        color="warning"
                      />
                    )}
                    label={t('template.show_unverified', 'Show unverified templates')}
                    sx={{
                      flexShrink: 0,
                      m: 0,
                      color: 'text.secondary',
                      '& .MuiFormControlLabel-label': { fontSize: '0.82rem' },
                    }}
                  />
                </Box>

                {/* Template Results Count */}
                {searchTerm.trim() && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 2, 
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      transition: 'opacity 0.3s ease',
                      animation: 'fadeIn 0.5s ease'
                    }}
                  >
                    {t('template.showing_results', { 
                      count: filteredTemplates.length,
                      total: availableTemplates.length
                    })}
                  </Typography>
                )}

                <Grid container spacing={2}>
                  {/* Keep the blank project outside filteredTemplates so it is always first and always visible. */}
                  {renderBlankProjectCard()}
                  {orderedFilteredTemplates.map((template) => {
                    const connectorNames = getTemplateConnectorNames(template, serverInfo?.connectorPlugins);
                    const dashboardTabs = getTemplateDashboardTabs(template);
                    const actionCount = getTemplateItemCount(template.actions);
                    const widgetCount = getTemplateItemCount(template.widgets);
                    const hasSelection = selectedTemplate?.id === template.id;
                    const isSharedTemplate = template.source_type === 'shared';
                    const { key: favoriteKey } = getTemplateFavoriteIdentity(template);
                    const isFavorite = favoriteTemplateKeySet.has(favoriteKey);
                    const isVerifiedTemplate = (
                      isSharedTemplate
                      && template.shared_verification_status === 'verified'
                    );

                    return (
                      <Grid item xs={12} sm={6} md={4} key={template.id}>
                        <Card
                          role="button"
                          tabIndex={0}
                          aria-pressed={hasSelection}
                          onClick={() => handleTemplateClick(template)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleTemplateClick(template);
                            }
                          }}
                          sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: 292,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            textAlign: 'left',
                            cursor: 'pointer',
                            border: hasSelection
                              ? `2px solid ${primaryColor}`
                              : '1px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: '12px',
                            transition: 'all 0.25s ease',
                            backgroundColor: hasSelection ? lightPurple : 'white',
                            '&:hover, &:focus-visible': {
                              borderColor: primaryColor,
                              boxShadow: '0 6px 20px rgba(87, 87, 176, 0.15)',
                              transform: 'translateY(-2px)',
                              outline: 'none',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              height: 96,
                              flexShrink: 0,
                              position: 'relative',
                              overflow: 'hidden',
                              background: 'linear-gradient(135deg, #7777d0 0%, #5757b0 100%)',
                            }}
                          >
                            {isSafeTemplateImage(template.template_image) ? (
                              <Box
                                component="img"
                                src={template.template_image}
                                alt={t('template.image_alt', 'Template preview')}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <TemplateIcon sx={{ color: 'rgba(255,255,255,0.86)', fontSize: 58, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                            )}
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(180deg, rgba(24, 24, 60, 0.04) 20%, rgba(24, 24, 60, 0.58) 100%)',
                              }}
                            />
                            {template.version && (
                              <Chip
                                label={`v${template.version}`}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  left: 12,
                                  bottom: 10,
                                  height: 22,
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  color: primaryColor,
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}
                            <Tooltip
                              title={isFavorite
                                ? t('template.remove_favorite', 'Remove from favorites')
                                : t('template.add_favorite', 'Add to favorites')}
                            >
                              <IconButton
                                aria-label={isFavorite
                                  ? t('template.remove_favorite', 'Remove from favorites')
                                  : t('template.add_favorite', 'Add to favorites')}
                                aria-pressed={isFavorite}
                                size="small"
                                onClick={(event) => handleToggleTemplateFavorite(event, template)}
                                onKeyDown={(event) => event.stopPropagation()}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  zIndex: 2,
                                  width: 32,
                                  height: 32,
                                  color: isFavorite ? '#ffd54f' : '#fff',
                                  backgroundColor: 'rgba(24, 24, 60, 0.46)',
                                  '&:hover': { backgroundColor: 'rgba(24, 24, 60, 0.72)' },
                                }}
                              >
                                {isFavorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            {!isSharedTemplate && (
                              <Tooltip title={t('template.template_actions', 'Template actions')}>
                                <IconButton
                                  aria-label={t('template.template_actions', 'Template actions')}
                                  size="small"
                                  onClick={(event) => handleTemplateMenuOpen(event, template)}
                                  onKeyDown={(event) => event.stopPropagation()}
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 2,
                                    width: 32,
                                    height: 32,
                                    color: '#fff',
                                    backgroundColor: 'rgba(24, 24, 60, 0.46)',
                                    '&:hover': { backgroundColor: 'rgba(24, 24, 60, 0.72)' },
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasSelection && (
                              <SelectedIcon
                                sx={{
                                  position: 'absolute',
                                  top: 10,
                                  right: isSharedTemplate ? 10 : 48,
                                  color: '#fff',
                                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
                                }}
                              />
                            )}
                          </Box>

                          <CardContent
                            sx={{
                              flexGrow: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              p: 2,
                              '&:last-child': { pb: 2 },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: primaryColor,
                                  fontSize: '1.05rem',
                                  lineHeight: 1.3,
                                }}
                              >
                                {getTemplateTitle(template) || t('template.untitled_template', 'Untitled template')}
                              </Typography>
                              <Chip
                                size="small"
                                icon={isSharedTemplate
                                  ? (isVerifiedTemplate
                                    ? <SelectedIcon />
                                    : <WarningAmberIcon />)
                                  : <LocalTemplateIcon />}
                                label={isSharedTemplate
                                  ? (isVerifiedTemplate
                                    ? t('template.verified', 'Verified')
                                    : t('template.unverified', 'Unverified'))
                                  : t('template.local', 'Local')}
                                color={isSharedTemplate
                                  ? (isVerifiedTemplate ? 'success' : 'warning')
                                  : 'default'}
                                variant={isSharedTemplate && !isVerifiedTemplate ? 'filled' : 'outlined'}
                                title={isSharedTemplate && !isVerifiedTemplate
                                  ? t('template.unverified_warning', 'This shared template has not been verified.')
                                  : undefined}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  '& .MuiChip-icon': { fontSize: 14 },
                                }}
                              />
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.82rem',
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 1.5,
                              }}
                            >
                              {getTemplateDescription(template) || t(
                                'template.description_fallback',
                                'A ready-made project starting point with reusable structure and settings.'
                              )}
                            </Typography>

                            <Box sx={{ mt: 'auto', pt: 1.25, borderTop: '1px solid rgba(87, 87, 176, 0.12)' }}>
                              <Box
                                component="section"
                                aria-label={t('template.connectors_used', 'Incoming data sources')}
                                sx={{
                                  p: 1,
                                  border: '1px solid rgba(87, 87, 176, 0.14)',
                                  borderRadius: 1.5,
                                  backgroundColor: 'rgba(87, 87, 176, 0.045)',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block', mb: 0.65, color: '#45457f', fontWeight: 700 }}
                                >
                                  {t('template.connectors_used', 'Incoming data sources')}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', minWidth: 0 }}>
                                  {connectorNames.length > 0 ? connectorNames.map((connectorName) => (
                                    <Chip
                                      key={normalizeConnectorKey(connectorName)}
                                      icon={<ConnectorIcon sx={{ fontSize: '14px !important' }} />}
                                      label={connectorName}
                                      title={connectorName}
                                      size="small"
                                      sx={{
                                        height: 23,
                                        maxWidth: '100%',
                                        fontSize: '0.68rem',
                                        backgroundColor: 'rgba(87, 87, 176, 0.1)',
                                        color: '#45457f',
                                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                                      }}
                                    />
                                  )) : (
                                    <Typography variant="caption" color="text.secondary">
                                      {t('template.connectors_missing_short', 'No connectors')}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {(actionCount > 0 || widgetCount > 0 || dashboardTabs.length > 0) && (
                                <Box
                                  component="section"
                                  aria-label={t('template.template_contents', 'Template contents')}
                                  sx={{ mt: 1, pt: 0.9, borderTop: '1px solid rgba(87, 87, 176, 0.1)' }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', mb: 0.65, color: 'text.secondary', fontWeight: 700 }}
                                  >
                                    {t('template.template_contents', 'Template contents')}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                                    {actionCount > 0 && (
                                      <Chip
                                        icon={<ActionIcon sx={{ fontSize: '14px !important' }} />}
                                        label={t('template.actions_count', '{{count}} actions', { count: actionCount })}
                                        size="small"
                                        sx={{ height: 23, fontSize: '0.68rem', backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                                      />
                                    )}
                                    {widgetCount > 0 && (
                                      <Chip
                                        icon={<WidgetIcon sx={{ fontSize: '14px !important' }} />}
                                        label={t('template.widgets_count', '{{count}} widgets', { count: widgetCount })}
                                        size="small"
                                        sx={{ height: 23, fontSize: '0.68rem', backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                                      />
                                    )}
                                    {dashboardTabs.length > 0 && (
                                      <Chip
                                        icon={<WidgetIcon sx={{ fontSize: '14px !important' }} />}
                                        label={t('template.dashboard_tabs_count', '{{count}} dashboard tabs', { count: dashboardTabs.length })}
                                        title={dashboardTabs.map((tab) => tab.name).join(', ')}
                                        size="small"
                                        sx={{ height: 23, fontSize: '0.68rem', backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                            </CardContent>
                          </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Menu
                  anchorEl={templateMenuAnchorEl}
                  open={Boolean(templateMenuAnchorEl && templateMenuTarget)}
                  onClose={handleTemplateMenuClose}
                  onClick={(event) => event.stopPropagation()}
                >
                  {canManageTemplateActions && (
                    <MenuItem
                      onClick={() => handleShareTemplate(templateMenuTarget)}
                      disabled={sharingTemplateId !== null || (
                        templateMenuTarget?.share_status === 'published'
                        && Number(templateMenuTarget?.version || 1) <= Number(templateMenuTarget?.shared_version || 0)
                      )}
                    >
                      <ListItemIcon>
                        {sharingTemplateId === templateMenuTarget?.id
                          ? <CircularProgress size={18} />
                          : <ShareIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText primary={t('global_setting.share_template_tooltip', 'Share this template for verification')} />
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => handleDownloadTemplate(templateMenuTarget)}
                    disabled={downloadingTemplateId !== null}
                  >
                    <ListItemIcon>
                      {downloadingTemplateId === templateMenuTarget?.id
                        ? <CircularProgress size={18} />
                        : <DownloadIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary={t('global_setting.export_template_tooltip', 'Export this template to JSON file')} />
                  </MenuItem>
                  {canManageTemplateActions && (
                    <>
                      <Divider />
                      <MenuItem
                        onClick={() => handleDeleteTemplate(templateMenuTarget)}
                        disabled={deletingTemplateId !== null}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon>
                          <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary={t('global_setting.delete_template', 'Delete Template')} />
                      </MenuItem>
                    </>
                  )}
                </Menu>

                {filteredTemplates.length === 0 && searchTerm.trim() && (
                  <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                    <TemplateIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography variant="h6" sx={{ mb: 0.75 }}>
                      {t('template.no_search_results')}
                    </Typography>
                    <Typography variant="body2">
                      {t('template.blank_project_always_visible', 'The blank project remains available above.')}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => setSearchTerm('')}
                      sx={{ mt: 1.5, color: primaryColor }}
                    >
                      {t('template.clear_search')}
                    </Button>
                  </Box>
                )}
              </>
            )}

            {!loading && !error && availableTemplates.length === 0 && (
              <>
                <Grid container spacing={2}>
                  {renderBlankProjectCard()}
                </Grid>
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <TemplateIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 0.75 }}>
                    {t('template.no_templates_found')}
                  </Typography>
                  <Typography variant="body2">
                    {t('template.create_template_first')}
                  </Typography>
                </Box>
              </>
            )}
        </>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          backgroundColor: 'rgba(250, 250, 255, 0.5)',
          borderTop: `1px solid ${lightPurple}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1.25,
        }}
      >
        {selectedTemplate && (
          <Box
            component="section"
            aria-label={t('template.selected_setup_summary', 'Selected template and project name')}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 0.85fr) minmax(0, 1.15fr)' },
              alignItems: 'center',
              gap: { xs: 0.75, md: 1.5 },
              p: { xs: 1, sm: 1.25 },
              borderRadius: 2,
              border: `1px solid ${primaryColor}35`,
              background: 'linear-gradient(135deg, #f4f3ff 0%, #fbfbff 100%)',
            }}
          >
            <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: primaryColor, fontWeight: 700, lineHeight: 1.1 }}>
                {t('template.selected_template', 'Selected template')}
              </Typography>
              <Typography variant="body2" sx={{ color: primaryColor, fontWeight: 700, overflowWrap: 'anywhere' }}>
                {getTemplateTitle(selectedTemplate)}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              label={t('template.project_title', 'Project name')}
              value={projectTitle}
              onChange={(event) => setProjectTitle(event.target.value)}
              disabled={creating}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && projectTitle.trim() && !creating) {
                  event.preventDefault();
                  handleCreateProject();
                }
              }}
              helperText={t('template.project_name_help', 'You can change this name later.')}
            />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', gap: 0.65, flexWrap: 'wrap', alignItems: 'center' }}>
                {!selectedTemplateSetupCounts.known ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('template.setup_summary_unknown', 'Any setup steps included in this template will appear after the project is created.')}
                  </Typography>
                ) : selectedTemplateSetupCounts.steps > 0 ? (
                  <>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label={t(
                        selectedTemplateSetupCounts.steps === 1
                          ? 'template.setup_summary_steps_one'
                          : 'template.setup_summary_steps',
                        selectedTemplateSetupCounts.steps === 1 ? '{{count}} setup step' : '{{count}} setup steps',
                        { count: selectedTemplateSetupCounts.steps },
                      )}
                    />
                    {selectedTemplateSetupCounts.sources > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(
                          selectedTemplateSetupCounts.sources === 1
                            ? 'template.setup_summary_sources_one'
                            : 'template.setup_summary_sources',
                          selectedTemplateSetupCounts.sources === 1
                            ? '{{count}} data source to prepare'
                            : '{{count}} data sources to prepare',
                          { count: selectedTemplateSetupCounts.sources },
                        )}
                      />
                    )}
                    {selectedTemplateSetupCounts.fields > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(
                          selectedTemplateSetupCounts.fields === 1
                            ? 'template.setup_summary_fields_one'
                            : 'template.setup_summary_fields',
                          selectedTemplateSetupCounts.fields === 1 ? '{{count}} field to fill' : '{{count}} fields to fill',
                          { count: selectedTemplateSetupCounts.fields },
                        )}
                      />
                    )}
                    {selectedTemplateSetupCounts.optional > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(
                          selectedTemplateSetupCounts.optional === 1
                            ? 'template.setup_summary_optional_one'
                            : 'template.setup_summary_optional',
                          selectedTemplateSetupCounts.optional === 1 ? '{{count}} optional step' : '{{count}} optional steps',
                          { count: selectedTemplateSetupCounts.optional },
                        )}
                      />
                    )}
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('template.setup_summary_none', 'No guided setup steps. You can open the project and adjust its settings anytime.')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={handleImportTemplate}
            disabled={loading || creating || importing}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            sx={{
              color: primaryColor,
              borderColor: 'rgba(87, 87, 176, 0.3)',
              '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.08)' }
            }}
            variant="outlined"
          >
            {importing ? t('template.importing', 'Importing...') : t('template.import', 'Import')}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              onClick={handleClose}
              disabled={creating}
              sx={{
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              {t('template.cancel')}
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={!selectedTemplate || !projectTitle.trim() || creating || loading}
              variant="contained"
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
              sx={{
                backgroundColor: primaryColor,
                '&:hover': { backgroundColor: hoverColor },
                '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
                borderRadius: '8px',
                px: 3,
              }}
            >
              {creating
                ? t('template.creating_project', 'Creating your project…')
                : selectedTemplateSetupCounts.steps > 0 || !selectedTemplateSetupCounts.known
                  ? t('template.create_and_configure', 'Create and configure')
                  : t('template.create_project', 'Create project')}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>

    <Dialog
      open={deleteDialogOpen}
      onClose={handleDeleteCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ color: primaryColor, fontWeight: 'bold' }}>
        {t('global_setting.confirm_delete_template', 'Delete Template')}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(
            'global_setting.delete_warning',
            'This action cannot be undone. The template will be permanently removed.',
          )}
        </Alert>
        <DialogContentText>
          {t(
            'global_setting.confirm_delete_template_message',
            'Are you sure you want to delete the template "{{templateName}}"?',
            { templateName: getTemplateTitle(templateToDelete) },
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleDeleteCancel} disabled={deletingTemplateId !== null}>
          {t('global_setting.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleDeleteConfirm}
          color="error"
          variant="contained"
          startIcon={deletingTemplateId !== null ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          disabled={deletingTemplateId !== null}
        >
          {deletingTemplateId !== null
            ? t('global_setting.deleting', 'Deleting...')
            : t('global_setting.delete_template', 'Delete Template')}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={duplicateDialogOpen}
      onClose={handleCancelledImport}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 28px rgba(87, 87, 176, 0.15)'
        }
      }}
    >
      <DialogTitle sx={{ color: primaryColor, fontWeight: 'bold' }}>
        {t('template.import_options', 'Import Options')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {pendingImportData?.type === 'multiple'
            ? t('template.import_multiple_message', {
                defaultValue: 'You are importing {{count}} templates. How should duplicates be handled?',
                count: pendingImportData?.data?.length || 0
              })
            : t('template.import_single_message', 'You are importing a template. How should duplicates be handled?')}
        </Typography>
        <Alert severity="info" sx={{ mb: 1 }}>
          {t(
            'template.skip_duplicates_description',
            'Skip duplicates is safer and prevents overwriting existing template names.'
          )}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCancelledImport} sx={{ color: 'text.secondary' }}>
          {t('template.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={() => handleConfirmedImport(false)}
          variant="outlined"
          sx={{ borderColor: '#d18b00', color: '#d18b00' }}
        >
          {t('template.import_all_allow_duplicates', 'Import All')}
        </Button>
        <Button
          onClick={() => handleConfirmedImport(true)}
          variant="contained"
          sx={{ backgroundColor: primaryColor, '&:hover': { backgroundColor: hoverColor } }}
        >
          {t('template.skip_duplicates', 'Skip Duplicates')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TemplateSelectionDialog;
